import { useEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import {
  Menu,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from '@/components/ui/menu';
import { useNamespaceCopy } from '@/i18n/ns';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import libraryFeedCopy from '@/i18n/ns/library-feed';
import { useMediaQuery } from '@/lib/mobile';
import { WorkspaceIcon } from '../icons';
import type { LibrarySegment } from './api';

const SEGMENTS: readonly LibrarySegment[] = ['all', 'mine', 'shared'];

/** "Owner: Anyone ▾": whose work the list shows (Anyone, Mine, Shared with me). */
function OwnerMenu({
  value,
  onChange,
}: {
  value: LibrarySegment;
  onChange: (segment: LibrarySegment) => void;
}) {
  const copy = useNamespaceCopy(libraryFeedCopy);
  const panels = useNamespaceCopy(artifactPanelsCopy);
  const label: Record<LibrarySegment, string> = {
    all: copy.ownerAnyone,
    mine: panels.librarySegmentMine,
    shared: panels.librarySegmentShared,
  };
  return (
    <Menu>
      <MenuTrigger asChild>
        <button type="button" className="ws-library-owner" data-testid="library-owner">
          <span className="ws-library-owner-label">{copy.owner}</span>
          <strong>{label[value]}</strong>
          <WorkspaceIcon name="down" size={14} />
        </button>
      </MenuTrigger>
      <MenuContent label={copy.owner} align="end">
        <MenuRadioGroup value={value} onValueChange={(next) => onChange(next as LibrarySegment)}>
          {SEGMENTS.map((segment) => (
            <MenuRadioItem key={segment} value={segment} data-segment={segment}>
              {label[segment]}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}

/** The top bar's page slot (shell.tsx), found from inside the page. */
function useTopbarSlot(anchor: RefObject<HTMLElement | null>, wanted: boolean) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setSlot(
      wanted
        ? (anchor.current?.closest('.ws-shell')?.querySelector<HTMLElement>('.ws-topbar-slot') ??
            null)
        : null,
    );
  }, [anchor, wanted]);
  return slot;
}

/** In the toolbar on a desktop; on a phone the full-width kind filter takes the row and the menu moves to the top bar. */
export function LibraryOwnerMenu({
  anchor,
  enabled,
  value,
  onChange,
}: {
  anchor: RefObject<HTMLElement | null>;
  /** False while the Library is not on screen: the top bar belongs to whatever is. */
  enabled: boolean;
  value: LibrarySegment;
  onChange: (segment: LibrarySegment) => void;
}) {
  const phone = useMediaQuery('(max-width: 640px)');
  const slot = useTopbarSlot(anchor, phone && enabled);
  const menu = <OwnerMenu value={value} onChange={onChange} />;
  if (!phone) return menu;
  return slot ? createPortal(menu, slot) : null;
}
